/**
 * Typed API for the per-character journal + per-series chronicle.
 *
 * Mounts under /api/companies/:cid/series/:sid/journal and /chronicle.
 * All functions accept the company-scoped Eden Treaty client returned by
 * useCompanyApi() so tenant isolation stays correct.
 */

import type { TCanonProposedBy, TCanonStatus, TChronicleEntryType, TJournalEntryType } from "@lexicanon/shared"
import type { CompanyApi } from "../../hooks/useCompanyApi.ts"
import { unwrapData } from "./errors.ts"

export type { TCanonProposedBy, TCanonStatus, TChronicleEntryType, TJournalEntryType }

// ---------------------------------------------------------------------------
// Public DTOs (mirror backend modules; Eden Treaty types are exact but verbose)
// ---------------------------------------------------------------------------

export interface IJournalEntry {
  id: string
  characterId: string
  seriesId: string
  meetingId: string | null
  entryType: TJournalEntryType
  title: string
  body: string
  sourceQuote: string | null
  sourceArtifactRef: string | null
  status: TCanonStatus
  proposedBy: TCanonProposedBy
  editedBody: string | null
  editedBy: string | null
  editedAt: string | null
  reassignedFrom: string | null
  createdAt: string | null
  /** Phase 8 — server-side enrichment. */
  meetingSubject: string | null
  meetingStartTime: string | null
  boundImageUrl: string | null
}

export interface IChronicleEntry {
  id: string
  seriesId: string
  meetingId: string | null
  entryType: TChronicleEntryType
  title: string
  body: string
  relatedCharacterIds: string[]
  relatedNpcs: string[]
  relatedLocations: string[]
  /** Resolved world-NPC FKs from the world-extraction step (Phase WS-2). Empty for pre-WS-2 entries. */
  relatedNpcIds: string[]
  /** Resolved world-location FKs from the world-extraction step (Phase WS-2). Empty for pre-WS-2 entries. */
  relatedLocationIds: string[]
  sourceQuote: string | null
  sourceArtifactRef: string | null
  status: TCanonStatus
  proposedBy: TCanonProposedBy
  editedBody: string | null
  editedBy: string | null
  editedAt: string | null
  occurredAt: string | null
  createdAt: string | null
  /** Meeting subject when the entry came from a session (Phase 8 server-side join). */
  meetingSubject: string | null
  /** Meeting start_time when the entry came from a session — used as session-label fallback. */
  meetingStartTime: string | null
  /** Plaque image URL when a memorable_moment with the same title produced one (Phase 5/8 join). */
  boundImageUrl: string | null
}

// ---------------------------------------------------------------------------
// Helpers — Eden Treaty's nested-tenant paths aren't in CompanyApi's auto-typed
// surface yet, so each call site casts the dynamic chain inline. Matches the
// pattern already used in meetings.ts for series-scoped routes. `unwrapData` is
// the shared helper from `errors.ts` that peels the `{ data }` envelope (Phase 4).
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Journal
// ---------------------------------------------------------------------------

export async function fetchJournalEntries(
  company: CompanyApi,
  seriesId: string,
  opts?: { status?: TCanonStatus; characterId?: string },
): Promise<IJournalEntry[]> {
  const query: Record<string, string> = {}
  if (opts?.status) query.status = opts.status
  if (opts?.characterId) query.characterId = opts.characterId
  const series = company.series({ id: seriesId })
  const result = await series.journal.get({ query })
  return unwrapData(result)
}

export async function setJournalStatus(
  company: CompanyApi,
  seriesId: string,
  entryId: string,
  status: "accepted" | "rejected",
): Promise<{ success: boolean }> {
  const series = company.series({ id: seriesId })
  const result = await series.journal({ entryId }).status.patch({ status })
  return unwrapData(result)
}

export async function editJournalEntry(
  company: CompanyApi,
  seriesId: string,
  entryId: string,
  patch: { title?: string; body?: string },
): Promise<{ success: boolean }> {
  const series = company.series({ id: seriesId })
  const result = await series.journal({ entryId }).patch(patch)
  return unwrapData(result)
}

export async function reassignJournalEntry(
  company: CompanyApi,
  seriesId: string,
  entryId: string,
  characterId: string,
): Promise<{ success: boolean }> {
  const series = company.series({ id: seriesId })
  const result = await series.journal({ entryId }).reassign.patch({ characterId })
  return unwrapData(result)
}

// ---------------------------------------------------------------------------
// Chronicle
// ---------------------------------------------------------------------------

export async function fetchChronicleEntries(
  company: CompanyApi,
  seriesId: string,
  opts?: { status?: TCanonStatus },
): Promise<IChronicleEntry[]> {
  const query: Record<string, string> = {}
  if (opts?.status) query.status = opts.status
  const series = company.series({ id: seriesId })
  const result = await series.chronicle.get({ query })
  return unwrapData(result)
}

export async function setChronicleStatus(
  company: CompanyApi,
  seriesId: string,
  entryId: string,
  status: "accepted" | "rejected",
): Promise<{ success: boolean }> {
  const series = company.series({ id: seriesId })
  const result = await series.chronicle({ entryId }).status.patch({ status })
  return unwrapData(result)
}

export async function editChronicleEntry(
  company: CompanyApi,
  seriesId: string,
  entryId: string,
  patch: { title?: string; body?: string },
): Promise<{ success: boolean }> {
  const series = company.series({ id: seriesId })
  const result = await series.chronicle({ entryId }).patch(patch)
  return unwrapData(result)
}
